import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, switchMap, catchError } from 'rxjs';
import { Note, CreateNoteRequest, UpdateNoteRequest, SetNotePinRequest } from '../models/note.model';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { NoteListStore } from '../../../state/note-list.store';
import { NoteListQuery } from '../../../state/note-list.query';
import { stripHtmlPreserveNewlines } from '../../../utils';

/**
 * Service for Notes Panel V2 API interactions.
 *
 * SCOPE: Loans only. Leads, applications, and partners continue to use V1.
 *
 * This service handles API calls only. Panel visibility is managed
 * by each parent component (Pipeline, URLA2, Dashboard).
 */
@Injectable({ providedIn: 'root' })
export class NotesPanelV2Service {
  private readonly _http = inject(HttpClient);
  private readonly _environment = inject(APP_ENV);
  private readonly _noteListQuery = inject(NoteListQuery);
  private readonly _noteListStore = inject(NoteListStore);

  private readonly _baseUrl: string;

  constructor() {
    this._baseUrl = this._environment.retail.apiUrl;
  }

  /**
   * Fetches all notes for a loan.
   * @param fileDataId The loan's fileDataId
   * @returns Observable of notes array
   */
  getNotes(fileDataId: number): Observable<Note[]> {
    if (!fileDataId) {
      return of([]);
    }

    return this._http.get<Note[]>(`${this._baseUrl}/loans/${fileDataId}/notes`);
  }

  /**
   * Creates a new note for a loan.
   * @param fileDataId The loan's fileDataId
   * @param request The note creation payload
   * @returns Observable of the created note
   */
  createNote(fileDataId: number, request: CreateNoteRequest): Observable<Note> {
    return this._http.post<Note>(`${this._baseUrl}/loans/${fileDataId}/notes`, request);
  }

  /**
   * Updates an existing note.
   * @param noteId The note GUID
   * @param request The update payload
   * @returns Observable of the updated note
   */
  updateNote(noteId: string, request: UpdateNoteRequest): Observable<Note> {
    return this._http.put<Note>(`${this._baseUrl}/notes/${noteId}`, request);
  }

  /**
   * Soft-deletes a note.
   * @param noteId The note GUID
   * @returns Observable of void
   */
  deleteNote(noteId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/notes/${noteId}`);
  }

  // ============================================================
  // Legacy Note Methods (V1 API)
  // ============================================================

  /**
   * Updates a legacy note via V1 API.
   * Legacy notes are stored in LoanColorMapping table (Notes, Notes2, Notes3, Notes4 columns).
   * Per BRD FR-2.2: All users can edit legacy notes, but no one can delete them.
   *
   * @param fileDataId The loan's fileDataId
   * @param content The updated note content
   * @returns Observable of void
   */
  updateLegacyNote(fileDataId: number, content: string): Observable<void> {
    // V1 API expects an array of note objects with byteFileDataID and content
    const payload = [
      {
        byteFileDataID: fileDataId,
        content: content,
      },
    ];

    return this._http.put<void>(`${this._baseUrl}/loannotes/batch`, payload);
  }

  // ============================================================
  // Tag/Category Methods
  // ============================================================

  /**
   * Syncs tags for a note (replaces all existing tags with the provided set).
   * @param noteGuid The note GUID
   * @param tagGuids Array of tag GUIDs to assign
   * @returns Observable of void (204 No Content)
   */
  syncNoteTags(noteGuid: string, tagGuids: string[]): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/notes/${noteGuid}/tags`, tagGuids);
  }

  // ============================================================
  // Pin Methods
  // ============================================================

  /**
   * Sets the pin state for a note.
   * @param noteId The note GUID
   * @param isPinned Whether to pin (true) or unpin (false)
   * @returns Observable of the updated note
   */
  setNotePinState(noteId: string, isPinned: boolean): Observable<Note> {
    const request: SetNotePinRequest = { isPinned };
    return this._http.patch<Note>(`${this._baseUrl}/notes/${noteId}/pin`, request);
  }

  // ============================================================
  // Unread Methods
  // ============================================================

  /**
   * Gets the unread count for a loan.
   * @param fileDataId The loan's fileDataId
   * @returns Observable of the unread count
   */
  getUnreadCount(fileDataId: number): Observable<number> {
    if (!fileDataId) {
      return of(0);
    }

    return this._http.get<number>(`${this._baseUrl}/loans/${fileDataId}/notes/unread-count`);
  }

  /**
   * Marks all notes as read for a loan.
   * @param fileDataId The loan's fileDataId
   * @returns Observable of void
   */
  markAsRead(fileDataId: number): Observable<void> {
    if (!fileDataId) {
      return of(undefined);
    }

    return this._http.post<void>(`${this._baseUrl}/loans/${fileDataId}/notes/mark-read`, {});
  }

  // ============================================================
  // Save Current Note (store-based, used by unsaved changes flow)
  // ============================================================

  /**
   * Saves the current note using state from NoteListStore (content + tags).
   * Handles create, update V2, and update legacy V1 paths with tag sync.
   * Resets the editor state on success.
   *
   * Used by QuickActionsUnsavedChangesService for "Save and Continue" flow
   * when the component may already be destroyed.
   */
  saveCurrentNote(): Observable<void> {
    const state = this._noteListQuery.getValue();
    const content = state.editorContent?.trim();

    const entityId = state.entityId as number;

    if (!content || !entityId) {
      return of(undefined);
    }
    const tagGuids = state.selectedTagGuids;

    if (state.isEditingLegacyNote) {
      // Legacy notes: strip HTML, no tag sync
      const plainText = stripHtmlPreserveNewlines(content);
      return this.updateLegacyNote(entityId, plainText).pipe(
        switchMap(() => {
          this._noteListStore.resetEditor();
          return of(undefined);
        }),
      );
    } else if (state.editingNoteGuid) {
      // Update existing V2 note + sync tags
      return this.updateNote(state.editingNoteGuid, { content }).pipe(
        switchMap(() => this.syncNoteTags(state.editingNoteGuid, tagGuids).pipe(catchError(() => of(undefined)))),
        switchMap(() => {
          this._noteListStore.resetEditor();
          return of(undefined);
        }),
      );
    } else {
      // Create new V2 note + sync tags if any
      return this.createNote(entityId, { content }).pipe(
        switchMap((newNote) => {
          if (tagGuids.length && newNote.guid) {
            return this.syncNoteTags(newNote.guid, tagGuids).pipe(catchError(() => of(undefined)));
          }
          return of(undefined);
        }),
        switchMap(() => {
          this._noteListStore.resetEditor();
          return of(undefined);
        }),
      );
    }
  }
}
